// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { ProfilePrivacyTelemetryObjects, profilePrivacyTelemetryActionDispatchers, profilePrivacyTelemetryContracts } from "./ProfilePrivacy.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import React from "react";
import { SocialProps } from "../../Social.props";
import { User } from "@msnews/social-data-service";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { hasThemeBase } from "../../utils/themeHelpers";

export interface ProfilePrivacyProps extends SocialProps {
    user: User;
}

export default class ProfilePrivacy extends React.Component<ProfilePrivacyProps> {
    divRef: React.RefObject<HTMLDivElement>;
    telemetryObjects: ProfilePrivacyTelemetryObjects;

    constructor(props) {
        super(props);
        this.divRef = React.createRef();
    }

    togglePrivateChecked(e, isPrivateProfile, telemetryObject) {
        this.props.profileConnector.setProfilePrivate(this.props.user.id, !isPrivateProfile);
        profilePrivacyTelemetryActionDispatchers.genericClicked(e.currentTarget, telemetryObject);
    }

    clearDataClickHandler(e) {
        this.props.popupConnector.openPopup("manageDataPage");
    }

    componentDidMount() {
        AccessibilityHelpers.attachAccessibilityListeners(document, this.divRef.current);
    }
    render() {
        const { responsiveClass, user, managedClasses, t, mainTelemetryObject, theme } = this.props;
        if (!user) { return null; }
        const { isPrivateProfile, traceId } = user;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);

        const ButtonWrapperComponent = isSuperApp ? AccentButton : OutlineButton;

        this.telemetryObjects = {
            privateSwitchTurnOff: addOrUpdateTelemetryObject(mainTelemetryObject, profilePrivacyTelemetryContracts.privateSwitchTurnOff, { id: traceId }, this.telemetryObjects && this.telemetryObjects.privateSwitchTurnOff),
            privateSwitchTurnOn: addOrUpdateTelemetryObject(mainTelemetryObject, profilePrivacyTelemetryContracts.privateSwitchTurnOn, { id: traceId }, this.telemetryObjects && this.telemetryObjects.privateSwitchTurnOn),
            manageYourDataBtn: addOrUpdateTelemetryObject(mainTelemetryObject, profilePrivacyTelemetryContracts.manageYourDataBtn, { id: traceId }, this.telemetryObjects && this.telemetryObjects.manageYourDataBtn)
        };

        return (
            <div className={
                classNames(managedClasses.profilePrivacyContainer,
                    [managedClasses.profilePrivacySuperAppContainer, isSuperApp],
                    responsiveClass
                )} ref={this.divRef}>
                <div className="cwt-private-switch">
                    <h6 className="cwt-title cwt-switch-label">{t.ProfilePagePrivateProfileTitle}</h6>
                    <label className="cwt-switch">
                        <input
                            type="checkbox"
                            aria-label={t.ProfilePagePrivateProfileTitle}
                            className="cwt-privacy-checkbox"
                            defaultChecked={isPrivateProfile}
                            onClick={(e) => this.togglePrivateChecked(e, isPrivateProfile, !isPrivateProfile ? this.telemetryObjects.privateSwitchTurnOff : this.telemetryObjects.privateSwitchTurnOn)}
                            {...renderHybrid(!isPrivateProfile ? this.telemetryObjects.privateSwitchTurnOff : this.telemetryObjects.privateSwitchTurnOn, false)}
                        />
                        <span className="cwt-switch-slider" />
                    </label>
                    <span className="cwt-on-label">
                        { isPrivateProfile ? `${t.ProfilePagePrivacySwitchOn}` : `${t.ProfilePagePrivacySwitchOff}`}
                    </span>
                </div>

                <p className="cwt-description">{t.ProfilePagePrivacySwitchText}</p>

                <hr className="cwt-h-seperator"/>

                <div className={classNames(
                    managedClasses.profileDataContainer,
                    [managedClasses.profileDataSuperAppContainer, isSuperApp]
                )}>
                    <h6 className="cwt-title">{t.ProfilePagePrivacyManageTitle}</h6>
                    <p className="cwt-description">{t.ProfilePagePrivacyManageText}</p>

                    <ButtonWrapperComponent
                        className={classNames(
                            "cwt-fast-button"
                        )}
                        onClick={this.clearDataClickHandler.bind(this)}
                        {...renderHybrid(this.telemetryObjects.manageYourDataBtn, false)}
                    >{t.ProfilePagePrivacyClearDataButton}</ButtonWrapperComponent>
                </div>
            </div>
        );
    }
}